'use client';
import dynamic from 'next/dynamic';
import ProjectDetailHero from '@/components/projectdetail-hero';
import ProjectSlider from '@/components/technologies/project-carousel';
import { use, useEffect, useState } from 'react';
import { isEmpty } from '@/utils/common-helpers';
import { useRouter } from 'next/navigation';
import FetchingRecords from '@/components/common/fetching';
import { getRequest } from '@/api/helpers';
import { routes } from '@/api/routes';
import Image from 'next/image';
import MetaTags from '@/components/meta-tag/meta-tag';
import ContentVideoSection from '@/components/common/content-video';
import AppProjectSlider from '@/components/technologies/app-carousel';
import AppProjectDetailHero from '@/components/app-projectdetail-hero';
import BorderEdge from '@/components/common/border-edge';

const ContactInfo = dynamic(() => import('@/components/common/contact-info'));
const ContactForm = dynamic(() => import('@/components/common/contact-form'));

const Page = ({ params }: { params: Promise<{ work: string }> }) => {
    const { work } = use(params);
    const router = useRouter();

    const [fetching, setFetching] = useState<boolean>(true);
    const [portfolio, setPortfolio] = useState<any>(null);

    useEffect(() => {
        if (isEmpty(work)) {
            router.push('/work');
        }

        fetchPortFolioDetail().then();
    }, [work]);

    const fetchPortFolioDetail = async () => {
        setFetching(true);
        const res = await getRequest(`${routes.PORTFOLIOS}/${work}`);

        if (isEmpty(res) || !res.status) {
            return router.push('/work');
        }

        setPortfolio(res.data);
        setFetching(false);
    };

    return (
        <>
            <MetaTags />
            {fetching || isEmpty(portfolio) ? (
                <FetchingRecords />
            ) : (
                <>
                    {portfolio.is_app_layout ? (
                        <AppProjectDetailHero
                            className=""
                            pageTitle={portfolio.name}
                            desc={portfolio.description_short}
                            alt="hero image"
                            title="hero image"
                            width={963}
                            height={506}
                            path={
                                !isEmpty(portfolio.hero_image)
                                    ? portfolio.hero_image
                                    : portfolio.portfolio_images.length > 0
                                      ? portfolio.portfolio_images[0].attachments
                                      : portfolio.avatar
                            }
                        />
                    ) : (
                        <ProjectDetailHero
                            className=""
                            pageTitle={portfolio.name}
                            desc={portfolio.description_short}
                            alt="hero image"
                            title="hero image"
                            width={963}
                            height={506}
                            path={
                                !isEmpty(portfolio.hero_image)
                                    ? portfolio.hero_image
                                    : portfolio.portfolio_images.length > 0
                                      ? portfolio.portfolio_images[0].attachments
                                      : portfolio.avatar
                            }
                        />
                    )}
                    <ContentVideoSection title="Project Overview">
                        <>
                            <p
                                className="fw-400 text-secondary fs-md"
                                style={{
                                    marginBottom: 30,
                                }}
                            >
                                {portfolio.description_long}
                            </p>

                            {!isEmpty(portfolio.website_url) && (
                                <a
                                    href={isEmpty(portfolio.website_url) ? '/' : portfolio.website_url}
                                    className="me-2"
                                    target="_blank"
                                >
                                    <Image
                                        src="/img/img-website@2x.webp"
                                        alt="website"
                                        className="mb-2"
                                        height={200}
                                        width={200}
                                        loading="lazy"
                                    />
                                </a>
                            )}

                            {!isEmpty(portfolio.android_app_url) && (
                                <a
                                    href={isEmpty(portfolio.android_app_url) ? '/' : portfolio.android_app_url}
                                    className="me-2"
                                    target="_blank"
                                >
                                    <Image
                                        src="/img/img-google-playstore@2x.webp"
                                        alt="play store"
                                        className="mb-2"
                                        height={200}
                                        width={200}
                                        loading="lazy"
                                    />
                                </a>
                            )}

                            {!isEmpty(portfolio.ios_app_url) && (
                                <a
                                    href={isEmpty(portfolio.ios_app_url) ? '/' : portfolio.ios_app_url}
                                    className="me-2"
                                    target="_blank"
                                >
                                    <Image
                                        src="/img/img-appstore@2x.webp"
                                        alt="app store"
                                        className="mb-2"
                                        height={200}
                                        width={200}
                                    />
                                </a>
                            )}
                        </>
                    </ContentVideoSection>
                    <>
                        {/* Technology Stack start */}
                        <section className="section-py bg-grey">
                            <div className="container xl:container mx-auto">
                                <div className="row flex items-center justify-center">
                                    <div className="col-lg-8 col-md-8 md:w-10/12 lg:w-8/12">
                                        <div className="flex row justify-center">
                                            <div className="col-lg-8 lg:w-8/12 px-4">
                                                <div className="mb-50 text-center">
                                                    <h2 className="fw-700 fs-xl mb-2.5">Technology Stack</h2>
                                                    <p className="">
                                                        We&apos;ve successfully applied these technologies and presenting this
                                                        kind of case study.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        {portfolio.technologies.length > 0 && (
                                            <div className="row flex flex-col md:flex-row md:flex justify-center items-center bs-gy-2.5">
                                                {portfolio.technologies.map((technology: any, index: number) => {
                                                    return (
                                                        <div className="w-full md:w-6/12 lg:w-3/12" key={index}>
                                                            <div className="card dev-service-card technology-stack bg-white dark:bg-black h-full text-center product-img">
                                                                <div className="card-body mb-0 py-5">
                                                                    <Image
                                                                        src={technology.avatar}
                                                                        alt={technology.technology_name}
                                                                        height={50}
                                                                        width={50}
                                                                    />
                                                                    <p className="card-text fw-700 fs-md text-textcolor">
                                                                        {technology.technology_name}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>
                        {portfolio.portfolio_images.length > 0 && portfolio.is_app_layout ? (
                            <AppProjectSlider images={portfolio.portfolio_images} />
                        ) : (
                            <ProjectSlider images={portfolio.portfolio_images} />
                        )}
                    </>
                    <BorderEdge color="text-white" bg="bg-blue" />
                    <div className="section bg-primary py-100 dark:text-light dark:bg-black">
                        <div className="container lg:container md:mx-auto">
                            <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                                <ContactForm />
                                <ContactInfo />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default Page;
